import { type IDraggableTreeNode } from '@framework/ui/components/tree/draggableTreeTypes.ts';
import { type IMapGroup } from '../../layers/MapGroup.ts';
import { type IMapLayer } from '../../layers/MapLayer.ts';

export type ConfigTreeNodeType = 'group' | 'layer';

/** Payload dominio per i nodi dell'albero di configurazione mappa */
export interface IConfigTreeNodeData {
  nodeType: ConfigTreeNodeType;
  groupData?: IMapGroup;
  layerData?: IMapLayer & { __uid?: string };
  layerIndex?: number;
  groupIndex?: number;
}

export type IConfigTreeNode = IDraggableTreeNode<IConfigTreeNodeData>;

/**
 * Converte i due array piatti (groups, layers) in una struttura ad albero
 * compatibile con Ant Design Tree.
 *
 * - Nodi gruppo: `key="group::{name}"`, con figli = layer del gruppo
 * - Nodi layer: `key="layer::{__uid}"`, `isLeaf=true`
 * - Layer senza gruppo valido → root level in fondo
 */
export function buildConfigTreeData(
  groups: IMapGroup[],
  layers: (IMapLayer & { __uid?: string })[],
): IConfigTreeNode[] {
  const groupNameSet = new Set(groups.map(group => group.name));
  const tree: IConfigTreeNode[] = [];

  const groupChildrenMap: Record<string, IConfigTreeNode[]> =
    Object.create(null);
  for (const group of groups) {
    groupChildrenMap[group.name] = [];
  }

  const ungrouped: IConfigTreeNode[] = [];

  layers.forEach((layer, index) => {
    const node: IConfigTreeNode = {
      key: `layer::${layer.__uid ?? index}`,
      title: layer.name,
      isLeaf: true,
      data: {
        nodeType: 'layer',
        layerData: layer,
        layerIndex: index,
      },
    };

    if (layer.group && groupNameSet.has(layer.group)) {
      groupChildrenMap[layer.group].push(node);
    } else {
      ungrouped.push(node);
    }
  });

  groups.forEach((group, index) => {
    tree.push({
      key: `group::${group.name}`,
      title: group.label || group.name,
      data: {
        nodeType: 'group',
        groupData: group,
        groupIndex: index,
      },
      children:
        groupChildrenMap[group.name].length > 0
          ? groupChildrenMap[group.name]
          : undefined,
    });
  });

  tree.push(...ungrouped);
  return tree;
}

/**
 * Ricostruisce l'array piatto di layer dall'albero dopo un'operazione di drop,
 * aggiornando il campo `group` di ogni layer.
 */
export function flattenTreeToLayers(
  treeData: IConfigTreeNode[],
): (IMapLayer & { __uid?: string })[] {
  const result: (IMapLayer & { __uid?: string })[] = [];

  for (const node of treeData) {
    if (node.data?.nodeType === 'group' && node.children) {
      for (const child of node.children) {
        if (child.data?.layerData) {
          result.push({
            ...child.data.layerData,
            group: node.data.groupData?.name,
          });
        }
      }
    } else if (node.data?.nodeType === 'layer' && node.data.layerData) {
      result.push({ ...node.data.layerData, group: undefined });
    }
  }

  return result;
}
